import { ProductsService } from '../services/products.service';

const productsState = {
  product: null,
  eventProducts: [],
  products: [],
  allStreams: [],
  allGoods: [],
  state: '',
  conf: {
    direction: 'asc',
    sort: 'name',
    type: 'stream',
    page: 1,
    per_page: 20,
    max_page: 1,
  },
  ordersConfig: {
    page: 1,
    per_page: 20,
    max_page: 1,
  },
  ordersState: '',
  orders: [],
  search: {
    state: '',
    result: [],
  },
  broadcastProducts: [],
};

const getters = {
  list: state => state.products,
  state: state => state.state,
  conf: state => state.conf,
  pagination: state => ({ page: state.conf.page, per_page: state.conf.per_page, max_page: state.conf.max_page }),
  product: state => state.product,
  allStreams: state => state.allStreams,
  allGoods: state => state.allGoods,
  orders: state => state.orders,
  ordersState: state => state.ordersState,
  ordersConfig: state => state.ordersConfig,
  ordersPagination: state => ({ page: state.ordersConfig.page, per_page: state.ordersConfig.per_page, max_page: state.ordersConfig.max_page }),
  searchState: state => state.search.state,
  searchResult: state => state.search.result,
  eventProducts: state => state.eventProducts,
  broadcastProducts: state => state.broadcastProducts,
};

const mutations = {
  setSearchState(state, type) {
    state.search.state = type;
  },
  setSearchResult(state, products) {
    state.search.result = products;
  },
  /**
   * Set the list state in state...
   * @param {Object} state
   * @param {String} type
   */
  setState(state, type) {
    state.state = type;
  },
  /**
   * Set the list array in state
   * @param {Object} state
   * @param {Array} products
   */
  setList(state, products) {
    state.products = products;
  },
  /**
   * Set the product in the state
   * @param {Object} state
   * @param {Object} product
   */
  setProduct(state, product) {
    state.product = product;
  },
  /**
   * Set the conf object in the state
   * @param {Object} state
   * @param {Object} conf
   */
  setConf(state, conf) {
    if (conf.type !== state.conf.type) {
      // eslint-disable-next-line
      conf.page = 1;
      // eslint-disable-next-line
      conf.max_page = 1;
    }
    state.conf = conf;
  },
  /**
   * Set the config object for orders list in the state
   * @param {Object} state
   * @param {Object} conf
   */
  setOrdersConfig(state, conf) {
    state.ordersConfig = conf;
  },
  /**
   * Set state for orders list
   * @param {Object} state
   * @param {String} type
   */
  setOrdersState(state, type) {
    state.ordersState = type;
  },
  /**
   * Set orders in orders list
   * @param {Object} state
   * @param {Array} orders
   */
  setOrders(state, orders) {
    state.orders = orders;
  },
  /**
   * Set the list of all streams
   * @param {Object} state
   * @param {Array} streams
   */
  setAllStreams(state, streams) {
    state.allStreams = streams;
  },
  /**
   * Set the list of all goods
   * @param {Object} state
   * @param {Array} goods
   */
  setAllGoods(state, goods) {
    state.allGoods = goods;
  },
  handleEventProduct(state, { payload: { id, variant_id = null }, timestamp, event }) {
    const hasProduct = state.eventProducts.findIndex(p => p.id === id);

    if (hasProduct < 0) {
      const product = {
        id,
        timestamps: [{ display_at: timestamp }],
      };

      if (variant_id) {
        product.timestamps[0].variant_id = variant_id;
      }
      state.eventProducts.push(product);
    } else {
      const product = state.eventProducts[hasProduct];
      if (event === 'product.show') {
        const tt = { display_at: timestamp };
        if (variant_id) {
          tt.variant_id = variant_id;
        }
        product.timestamps.push(tt);
      }
      if (event === 'product.hide') {
        const hideIndex = product.timestamps.findIndex(t => (variant_id ? t.variant_id === variant_id && !t.hide_at : !t.hide_at && !t.variant_id));
        if (hideIndex >= 0) {
          product.timestamps[hideIndex].hide_at = timestamp;
        }
      }

      state.eventProducts[hasProduct] = product;
    }
  },
  clearEventProducts(state) {
    state.eventProducts = [];
  },
  /**
   * Set the productslist of a broadcast
   * @param {Object} state
   * @param {Array} products
   */
  setBroadcastList(state, products) {
    state.broadcastProducts = products;
  },
};

const actions = {
  /**
   * Resets the product object to null
   * @param {Object} obj
   * @param {Function} obj.commit
   */
  resetProduct: async ({ commit }) => {
    commit('setProduct', null);
  },
  /**
   * Get and set a product
   * @param {Object} obj
   * @param {Function} obj.commit
   * @param {String} id
   * @returns {Object|Boolean}
   */
  getProduct: async ({ commit }, id) => {
    try {
      const product = await ProductsService.getProduct(id);
      commit('setProduct', product);
      return product;
    } catch (e) {
      return false;
    }
  },
  /**
   * Get and set a product
   * @param {Object} obj
   * @param {Function} obj.commit
   * @param {Object} obj.state
   * @param {Function} obj.dispatch
   * @param {Object} params
   * @returns {Array|Boolean}
   */
  getProducts: async ({ commit, state, dispatch }, params = {}) => {
    let payload;
    if (!params.skipConf) {
      payload = JSON.parse(JSON.stringify(state.conf));
      delete payload.max_page;
    } else {
      payload = JSON.parse(JSON.stringify(params));
      delete payload.skipConf;
    }
    if (params.skipState) {
      delete payload.skipState;
    }

    try {
      if (!params.skipLoading) {
        commit('setState', 'loading');
      }
      const products = await ProductsService.getProducts(payload);
      if (!params.skipState) {
        commit('setList', products.products);
      }
      commit('setState', '');
      if (!params.skipConf) {
        dispatch('updateConf', {
          page: products.pagination.current_page,
          max_page: products.pagination.max_page,
        });
      }
      return products;
    } catch (e) {
      return false;
    }
  },
  /**
   * Update the list conf
   * @param {Object} obj
   * @param {Function} obj.commit
   * @param {Object} obj.state
   * @param {Object} options
   */
  updateConf: ({ commit, state }, options) => {
    const conf = Object.assign(JSON.parse(JSON.stringify(state.conf)), options);
    commit('setConf', conf);
  },
  /**
   * Get all tickets (stream type)
   * @param {Object} obj
   * @param {Function} obj.commit
   * @returns {Array}
   */
  getAllStreams: async ({ commit }) => {
    try {
      const streams = await ProductsService.getAllStreams();
      commit('setAllStreams', streams);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Get all goods (good type)
   * @param {Object} obj
   * @param {Function} obj.commit
   * @returns {Array}
   */
  getAllGoods: async ({ commit }) => {
    try {
      const streams = await ProductsService.getAllGoods();
      commit('setAllGoods', streams);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Create a product
   * @param {Object} obj
   * @param {Object} payload
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  createProduct: async ({}, payload) => {
    try {
      const { data: product } = await ProductsService.createProduct(payload);
      return product;
    } catch (e) {
      return false;
    }
  },
  /**
   * Update product
   * @param {Object} obj
   * @param {Function} obj.commit
   * @param {Object} data
   * @param {String} data.id
   * @param {Object} data.payload
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  updateProduct: async ({ }, { id, payload}) => {
    try {
      const product = await ProductsService.updateProduct(id, payload);
      return product;
    } catch (e) {
      return false;
    }
  },
  /**
   * Delete product
   * @param {Object} obj
   * @param {String} id
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  deleteProduct: async ({ }, id) => {
    try {
      await ProductsService.deleteProduct(id);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Update product
   * @param {Object} obj
   * @param {Function} obj.commit
   * @param {Object} data
   * @param {String} data.id
   * @param {Object} data.payload
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  updateAttributes: async ({ }, { id, payload }) => {
    try {
      const attributes = await ProductsService.addAttributeToProduct(id, payload, true);
      return attributes;
    } catch (e) {
      return false;
    }
  },
  /**
   * Update content
   * @param {Object} obj
   * @param {Object} data
   * @param {String} data.id
   * @param {Object} data.payload
   * @param {Object} data.patch
   */
  // eslint-disable-next-line
  updateContent: async ({}, { id, payload, patch = false }) => {
    try {
      const content = await ProductsService.updateContent(id, payload, patch);
      return content;
    } catch (e) {
      return false;
    }
  },
  /**
   * Delete content
   * @param {Object} obj
   * @param {Object} data
   * @param {String} data.id
   * @param {Object} data.payload
   * @param {Object} data.patch
   */
  // eslint-disable-next-line
  deleteContent: async ({}, { id, payload }) => {
    try {
      const content = await ProductsService.removeContent(id, payload);
      return content;
    } catch (e) {
      return false;
    }
  },
  /**
   * Create price
   */
  // eslint-disable-next-line
  createSKU: async ({}, payload) => {
    try {
      const sku = await ProductsService.createSKU(payload);
      return sku;
    } catch (e) {
      return false;
    }
  },
  /**
   * Update price
   */
  // eslint-disable-next-line
  updateSKU: async ({}, { id, payload }) => {
    try {
      const sku = await ProductsService.updateSKU(id, payload);
      return sku;
    } catch (e) {
      return false;
    }
  },
  /**
   * Delete price
   */
  // eslint-disable-next-line
  deleteSKU: async ({}, id) => {
    try {
      await ProductsService.deleteSKU(id);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Add image
   */
  // eslint-disable-next-line
  addImage: async ({}, { id, images }) => {
    try {
      await ProductsService.addImage(id, images);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Remove image
   */
  // eslint-disable-next-line
  removeImage: async ({}, { id, images }) => {
    try {
      await ProductsService.removeImage(id, images);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Update images
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  updateImages: async ({}, { id, images }) => {
    try {
      await ProductsService.updateImages(id, images);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Get orders
   * @param {Object} obj
   * @param {Function} obj.commit
   * @param {Function} obj.dispatch
   * @param {Object} obj.state
   * @param {Object} params
   * @return {Boolean}
   */
  getOrders: async ({ commit, dispatch, state }, params = {}) => {
    let payload;
    if (!params.skipConf) {
      payload = JSON.parse(JSON.stringify(state.ordersConfig));
      delete payload.max_page;
    } else {
      payload = JSON.parse(JSON.stringify(params));
      delete payload.skipConf;
    }

    try {
      if (!params.skipLoading) {
        commit('setOrdersState', 'loading');
      }
      const data = await ProductsService.getOrders(payload);
      commit('setOrders', data.orders);
      commit('setOrdersState', '');
      if (!params.skipConf) {
        dispatch('updateOrdersConfig', {
          page: data.pagination.current_page,
          max_page: data.pagination.max_page,
        });
      }
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * @param {Object} emptyObject
   * @param {String} id
   * @returns {?Object}
   */
  // eslint-disable-next-line
  getOrder: async ({ }, id) => {
    try {
      const data = await ProductsService.getOrder(id);
      return data;
    } catch (e) {
      return false;
    }
  },
  /**
   * @param {Object} emptyObject
   * @param {Object} payload
   * @param {String} payload.id
   * @param {String} payload.tracking
   * @param {String} payload.carrier
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  fulfillOrder: async ({ }, { id, tracking, carrier }) => {
    try {
      await ProductsService.fulfillOrder(id, { tracking, carrier });
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Update the orders list conf
   * @param {Object} obj
   * @param {Function} obj.commit
   * @param {Object} obj.state
   * @param {Object} options
   */
  updateOrdersConfig: ({ commit, state }, options) => {
    const conf = Object.assign(JSON.parse(JSON.stringify(state.ordersConfig)), options);
    commit('setOrdersConfig', conf);
  },
  /**
   * Save shopify feed
   * @param {*} param0 empty
   * @param {String} store
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  saveShopifyFeed: async ({}, store) => {
    try {
      await ProductsService.saveShopifyStore(store);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Save magento feed
   * @param {*} param0 empty
   * @param {Object} data
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  saveMagentoFeed: async ({}, data) => {
    try {
      await ProductsService.saveMagentoFeed(data);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Save google merchant feed
   * @param {*} param0 empty
   * @param {String} feed
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  saveGoogleMerchantFeed: async ({}, feed) => {
    try {
      await ProductsService.saveGoogleMerchantFeed(feed);
      return 'refresh';
    } catch (e) {
      return false;
    }
  },
  /**
   * Save custom feed
   * @param {*} param0 empty
   * @param {String} feed
   * @returns {Boolean}
   */
  saveProductLinks: async () => {
    try {
      await ProductsService.saveProductLinks();
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Save centra feed
   * @param {*} param0 empty
   * @param {String} feed
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  saveCentra: async ({}, feed) => {
    try {
      await ProductsService.saveCentra(feed);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Save geins feed
   * @param {*} param0 empty
   * @param {String} feed
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  saveGeins: async ({}, feed) => {
    try {
      await ProductsService.saveGeins(feed);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Refresh google merchant feed
   * @param {*} param0 empty
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  refreshGoogleMerchantFeed: async ({}) => {
    try {
      await ProductsService.refreshGoogleMerchantFeed();
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Search for products in our amazing API
   * @param {Object} obj
   * @param {Function} obj.commit
   * @param {String} searchKey
   * @returns {Array}
   */
  searchProducts: async ({ state, commit, rootGetters }, searchKey) => {
    const shopSettings = rootGetters['AccountManager/shoppingSettings'];
    const provider = shopSettings && (shopSettings.provider || false);

    commit('setSearchState', 'searching');
    // eslint-disable-next-line
    if (!!provider) {
      try {
        const unfilteredProducts = await ProductsService.searchProducts(searchKey);
        const products = unfilteredProducts.map(product => ({ ...product, addHandle: product.id }));
        commit('setSearchResult', products);
      } catch (e) {
        commit('setSearchResult', []);
      }
    } else {
      const { allGoods } = state;
      const foundProducts = allGoods
        .filter(good => good.name.toLowerCase().includes(searchKey))
        .map((good) => {
          const price = good.prices ? good.prices.find(p => p.active) : null;
          return {
            id: good.id,
            title: good.name,
            description: good.description,
            image: good.images[0] || null,
            addHandle: good.id,
            type: good.type, // Just so we know that it aint from shopify or google merchant
            price: price ? price.formated_price : '',
            price_range: price ? price.formated_price : '',
          };
        });
      commit('setSearchResult', foundProducts);
    }

    commit('setSearchState', 'searched');
  },
  clearSearchProducts: ({ commit }) => {
    commit('setSearchResult', []);
    commit('setSearchState', '');
  },
  /**
   * Handle a message from the eventstream
   * @param {Object} param0
   * @param {Function} param0.commit
   * @param {Function} param0.dispatch
   * @param {Object} param1
   * @param {String} param1.id
   * @param {Object} param1.message
   */
  handleMessage: async ({ commit, dispatch, getters: gitters }, { id, message }) => {
    if (message.stream === 'broadcast' && (message.event === 'product.show' || message.event === 'product.hide')) {
      if (message.payload.id !== '') {
        /*
        const eventProductObj = {
          id: message.payload.id,
          show: message.event === 'product.show',
        };
        if (message.event === 'product.show') {
          eventProductObj.displayAt = message.timestamp;
        } else if (message.event === 'product.hide') {
          eventProductObj.hideAt = message.timestamp;
        }
        */

        commit('handleEventProduct', message);

        const [product] = gitters['broadcastProducts'].filter(p => p.id === message.payload.id);

        let sku = null;
        if (message.payload.variant_id) {
          sku = product.variants.find(v => v.variant_id === message.payload.variant_id);
        }

        commit('Chat/addMessage', {
          id,
          message: {
            message_id: message.message_id,
            event: 'chat.product',
            show: message.event === 'product.show',
            timestamp: message.timestamp,
            product: sku || product,
            message: `${sku ? sku.title : product.title} ${message.event === 'product.show' ? 'exposing' : 'unexposed'}`,
          },
        }, { root: true });
      }
    }
  },
  // eslint-disable-next-line
  toggleEventProduct: async ({}, { id, payload }) => {
    try {
      await ProductsService.toggleEventProduct(id, payload);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Get products that are tied to a broadcast
   * @param {Object}    param0
   * @param {Function}  param0.commit
   * @param {String}    id
   * @returns {Array}
   */
  listBroadcastProducts: async ({ commit }, id) => {
    try {
      const products = await ProductsService.getBroadcastProducts(id);
      commit('setBroadcastList', products);
      return true;
    } catch (e) {
      commit('setBroadcastList', []);
      return false;
    }
  },
  // eslint-disable-next-line
  updateProductOnBroadcast: async ({}, { id, payload }) => {
    try {
      await ProductsService.updateProductOnBroadcast(id, payload);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Add product(s) to broadcast
   * @param {Object}    param0
   * @param {Object}    param1
   * @param {String}    param1.id
   * @param {Array}     param1.arr
   * @returns {Boolean}
   */
  // eslint-disable-next-line
  addProductsToBroadcast: async ({}, { id, arr }) => {
    try {
      await ProductsService.addProductsToBroadcast(id, arr);
      return true;
    } catch (e) {
      return false;
    }
  },
  // eslint-disable-next-line
  scrapeProduct: async ({}, url) => {
    try {
      const products = await ProductsService.scrapeProducts(url);
      return products;
    } catch (e) {
      return false;
    }
  },
  // eslint-disable-next-line
  addScrapedProductToBroadcast: async ({}, { id, arr }) => {
    try {
      await ProductsService.addScrapedProductToBroadcast(id, arr);
      return true;
    } catch (e) {
      return false;
    }
  },
  // eslint-disable-next-line
  sortProductsOnBroadcast: async ({}, { id, arr }) => {
    try {
      await ProductsService.sortProductsOnBroadcast(id, arr);
      return true;
    } catch (e) {
      return false;
    }
  },
  // eslint-disable-next-line
  removeProductFromBroadcast: async ({}, { id, arr }) => {
    try {
      await ProductsService.removeProductsFromBroadcast(id, arr);
      return true;
    } catch (e) {
      return false;
    }
  },
};

export default {
  namespaced: true,
  state: productsState,
  mutations,
  actions,
  getters,
};
